import React, { useEffect } from "react";
import { Stack, Grid, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";

import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { createLicense } from "../redux/actions/licenseActions";
import { getCustomers } from "../redux/actions/customerActions";
import { getProducts } from "../redux/actions/productActions";
import { getTaxCodes } from "../redux/actions/taxCodeActions";
import { cleanFormData } from "../helpers/formHelpers";

const schema = Yup.object({
  productId: Yup.string().required("Product is required"),
  customerId: Yup.string().required("Customer is required"),
  type: Yup.string().required("License type is required"),
  taxCodeId: Yup.string().required("Tax code is required"),
  renewalPrice: Yup.number().required("Renewal price is required").min(0),
  clientInfo: Yup.string().nullable(),
  expiryDate: Yup.date()
    .nullable()
    .min(new Date(), "Expiry date must be in the future"),
});

const CreateLicenseForm = ({ loading }) => {
  const dispatch = useDispatch();

  const { customers } = useSelector((state) => state.customer);
  const { products } = useSelector((state) => state.product);
  const { taxCodes } = useSelector((state) => state.taxCode);

  useEffect(() => {
    if (!customers || customers.length === 0)
      dispatch(getCustomers({ limit: 500 }));
    if (!products || products.length === 0) dispatch(getProducts());
    if (!taxCodes || taxCodes.length === 0) dispatch(getTaxCodes());
  }, [customers, dispatch, products, taxCodes]);

  const customerOpts = (customers?.content || customers || []).map((c) => ({
    value: c.id,
    label: c.businessName,
  }));
  const productOpts = (products || []).map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const taxOpts = (taxCodes || []).map((t) => ({
    value: t.id,
    label: `${t.name} (${t.rate}%)`,
  }));

  const typeOpts = [
    { value: "TRIAL", label: "Trial" },
    { value: "MONTHLY", label: "Monthly" },
    { value: "ANNUAL", label: "Annual" },
  ];

  return (
    <Formik
      initialValues={{
        productId: "",
        customerId: "",
        type: "",
        taxCodeId: "",
        renewalPrice: "",
        clientInfo: "",
        expiryDate: "",
      }}
      validationSchema={schema}
      onSubmit={(values) => dispatch(createLicense(cleanFormData(values)))}
    >
      {({ isValid }) => (
        <Form>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormSelectField
                  name="customerId"
                  label="Customer"
                  options={customerOpts}
                  searchable
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSelectField
                  name="productId"
                  label="Product"
                  options={productOpts}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSelectField
                  name="type"
                  label="License Type"
                  options={typeOpts}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormSelectField
                  name="taxCodeId"
                  label="Tax Code"
                  options={taxOpts}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="renewalPrice"
                  label="Renewal Price"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormTextField
                  name="expiryDate"
                  label="Custom Expiry Date (Optional)"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  name="clientInfo"
                  label="Client Info / Machine ID Details"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
            <SubmitButton
              title="Generate License"
              loading={loading}
              disabled={!isValid || loading}
              startIcon={<AddCircleOutlineIcon />}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
export default CreateLicenseForm;
